import { useState, useEffect } from 'react'

type NavigationLayoutType =
  | 'single_column'
  | 'two_columns_with_an_image'
  | 'three_columns'
  | 'basic_nav_layout'
  | 'single_column_with_image'

interface NavigationSection {
  component: NavigationLayoutType
  [key: string]: unknown
}

interface NavigationLayoutStrategy {
  process(item: any): Record<string, any>
}

class SingleColumnStrategy implements NavigationLayoutStrategy {
  process(item: any): Record<string, any> {
    return {
      ...item,
      open: false,
      navigation_sections:
        item.items?.length > 1
          ? item.items.map((section: any) => ({
              ...section,
            parent_component: item.component,
              open: false,
            }))
          : item.items?.length === 1
            ? item.items?.map((link: any) => ({
                ...link,
                links: [],
                parent_component: item.component,
              }))
            : [],
    }
  }
}

class TwoColumnsWithImageStrategy implements NavigationLayoutStrategy {
  process(item: any): Record<string, any> {
    const two_cols = {
      ...item,
      open: false,
      navigation_sections:
        [...(item.first_column_item || []), ...(item.second_column_items || [])].length > 0
          ? [...(item.first_column_item || []), ...(item.second_column_items || [])].map((section: any) => ({
              ...section,
              open: false,
              parent_component: item.component,
              group_label: section.first_column_label || section.single_column_label,
            }))
          : [],
    }

    return two_cols
  }
}

class ThreeColumnsStrategy implements NavigationLayoutStrategy {
  process(item: any): Record<string, any> {
    return {
      ...item,
      open: false,
      navigation_sections:
        item.items?.length > 0
          ? item.items.flatMap(
              (column: any) =>
                column.items?.map((section: any) => ({
                  ...section,
                  menu_label: section.label,
                  parent_component: item.component,
                  open: false,
                })) || [],
            )
          : [],
    }
  }
}

class BasicNavLayoutStrategy implements NavigationLayoutStrategy {
  process(item: any): Record<string, any> {
    return {
      ...item,
      open: false,
      navigation_sections:
        item.items?.length > 0
          ? item.items.map((section: any) => ({
              ...section,
              parent_component: item.component,
              open: false,
            }))
          : [],
    }
  }
}

class SingleColumnWithImageStrategy implements NavigationLayoutStrategy {
  process(item: any): Record<string, any> {
    // insert if there's image
    return {
      ...item,
      open: false,
      image: item.image,
      navigation_sections:
        item.links?.length > 0
          ? item.links.map((section: any) => ({
              ...section,
              open: false,
            }))
          : [],
    }
  }
}

const layoutStrategies: Record<NavigationLayoutType, NavigationLayoutStrategy> =
  {
    single_column: new SingleColumnStrategy(),
    two_columns_with_an_image: new TwoColumnsWithImageStrategy(),
    three_columns: new ThreeColumnsStrategy(),
    basic_nav_layout: new BasicNavLayoutStrategy(),
    single_column_with_image: new SingleColumnWithImageStrategy(),
  }

type NavDataType = {
  _uid: string
  name: string
  target: string
  children: any[]
  open: boolean
  navigation_sections?: Array<NavigationSection>
}

type UseExtractNavDataProps = {
  data: any
}

export const useExtractNavData = ({ data }: UseExtractNavDataProps) => {
  const [navData, setNavData] = useState<NavDataType[]>([])

  useEffect(() => {
    if (!data) return

    const extractedData = data.map((item: any) => {
      const strategy = layoutStrategies[item.component as NavigationLayoutType]
      return strategy ? strategy.process(item) : []
    })

    setNavData(extractedData || [])
  }, [data])

  return {
    navData,
    setNavData,
  }
}
